import { DataSource } from './source';
import { DataSourceOptions, TypedDatumDefinition } from './types';
import { VisualizerControl } from './visualizer';

import { Application } from '../application/application';
import { Timeline } from '../timeline/timeline';
import { TimelineRange, CursorMillis } from '../timeline/timeline-common';
import { Subscription } from 'rxjs';

export interface RequestStackItem {
	timeRange?: TimelineRange,
	animationTiming?: CursorMillis[],
	types: string[],
	refresh: boolean,
	noAbort: boolean,
	unmutable: boolean
}

export abstract class PullDataSource extends DataSource {

	private requestStack: RequestStackItem[];

	public constructor(options: DataSourceOptions, application: Application) {
		super(options, application);
		this.requestStack = [];
	}

	/**
	 * Called by application when main timeline becomes available. Propagates to the
	 * viewers
	 */
	public setTimeline(timeline: Timeline) {
		super.setTimeline(timeline);
		if (this.getOptions().notTemporal !== true) {
			this.requestData();
		}
	}

	protected subscribeToTimeline(): Subscription[] {
		return super.subscribeToTimeline().concat([
			this.getTimeline().getObservable('mapp-tl-rng').subscribe(timeRange => {
				if (this.getOptions().notTemporal !== true) {
					this.requestData(timeRange, this.getTimeline().getAnimationTiming());
				}
			})
		]);
	}
	
	public addType(type: TypedDatumDefinition, controls: VisualizerControl): string {
		let isNew = this.getTypes().indexOf(type.id) == -1;
		let result = super.addType( type, controls );
//		if( isNew ){
//			this.demandData(type.id, this.getMeta().timeRange, this.getMeta().animationTiming);
//		}

		return result;
	}
	
	protected typeAdded(typeId: string): void{
			this.demandData(typeId, this.getMeta().timeRange, this.getMeta().animationTiming);
	}

	public refresh(type: string): void {
		this.demandData(type, this.getMeta().timeRange, this.getMeta().animationTiming);
	}

	protected requestData(timeRange?: TimelineRange, animationTiming?: CursorMillis[]): void {
		if (this.isReady()) {

			//determine the time range and map bounds to use in the new request
			let _timeRange = this.getOptions().notTemporal !== true ? timeRange ? timeRange : this.getTimeline().getTimeRange() : null;
			let _animationTiming = this.getOptions().notTemporal !== true ? animationTiming ? animationTiming : this.getTimeline().getAnimationTiming() : null;


			let lastStackItem: RequestStackItem = this.requestStack.length > 0 ? this.requestStack[this.requestStack.length - 1] : null;

			if (lastStackItem && !lastStackItem.unmutable) {
				lastStackItem.timeRange = _timeRange;
				lastStackItem.animationTiming = _animationTiming;
				lastStackItem.types = this.getTypes().concat([]);
			} else {
				this.requestStack.push({
					timeRange: _timeRange,
					animationTiming: _animationTiming,
					types: this.getTypes().concat([]),
					refresh: false,
					noAbort: false,
					unmutable: false
				});
			}
			this.doDataRequest();
		}

	}

	private demandData(body: string, timeRange: TimelineRange, animationTiming: CursorMillis[]): void {
		if (this.isReady()) {

			//determine the time range and map bounds to use in the new request
			let _timeRange = this.getOptions().notTemporal !== true ? timeRange ? timeRange : this.getTimeline().getTimeRange() : null;
			let _animationTiming = this.getOptions().notTemporal !== true ? animationTiming ? animationTiming : this.getTimeline().getAnimationTiming() : null;

			this.requestStack.splice(0, 0, {
				timeRange: _timeRange,
				animationTiming: _animationTiming,
				types: [body],
				refresh: true,
				noAbort: true,
				unmutable: true
			});
			this.doDataRequest();
		}
	}

	protected abstract doNextDataRequest(): Promise<{ request: RequestStackItem, data: any }>;
	
	protected abortDataRequest(): void {	}

	protected doDataRequest(): void {

		if (this.isReady() && !this.isRequestStackEmpty()) {

			//abort running data request when the options has been configured that way and a request is running
			if (this.getOptions().abortDataRequest !== false) {
				this.abortDataRequest();
			}

			this.doNextDataRequest()
				.then((result) => {
					this.handleData(result.request.timeRange, result.request.animationTiming, result.request.types, result.data);
					this.doDataRequest();
				}).catch((error) => {
					if(error.status !=401 && error.status!=403){
						console.error(error);
					}
				});

		}
	}

	protected isRequestStackEmpty(): boolean {
		return this.requestStack.length == 0;
	}
	protected popRequest(): RequestStackItem {
		return this.requestStack.length > 0 ? this.requestStack.splice(0, 1)[0] : undefined;
	}
	protected getRequestedTypes(request: RequestStackItem): string[] {
//		return request.types == null ? undefined : request.types.filter(type => this.getDataList(type) != null).map(type => `${type}::_d`);
		return request.types == null ? undefined : request.types.filter(type => this.getDataList(type) != null);
	}

}